/**
 * Document classifier adapter, backed by onnxruntime-web. Runs the GPU-trained
 * doc-cnn (MobileNetV3 → ONNX) model: takes the CHW float32 tensor the
 * {@link DocClassifier} builds and returns the raw class logits.
 *
 * Same lazy-load / wasm-path pattern as the pre-processing adapters
 * (OnnxU2netp, YunetFaceDetector): the session loads on first use, and
 * `ort.env.wasm.wasmPaths` is set when provided so the wasm backend can fetch
 * its binaries in the browser. onnxruntime-web is a runtime dependency (not
 * Angular), so this adapter is browser/Node-portable.
 */
import * as ort from 'onnxruntime-web';
import { ClassifierModel } from '../ports';

export interface OnnxClassifierOptions {
  /** URL/path to the classifier ONNX model (default 'doc-cnn.onnx', site root). */
  modelUrl?: string;
  /** Execution providers, e.g. ['webgpu','wasm']. Default ['wasm']. */
  executionProviders?: ('webgpu' | 'wasm' | 'cpu')[];
  /** Where onnxruntime-web should fetch its wasm binaries (browser). */
  wasmPaths?: string;
  /** Input tensor name in the ONNX graph (default 'input'). */
  inputName?: string;
  /** Output (logits) tensor name in the ONNX graph (default 'logits'). */
  outputName?: string;
  /** Pre-created session (skips loading). */
  session?: ort.InferenceSession;
}

export class OnnxClassifier implements ClassifierModel {
  private session?: ort.InferenceSession;
  private loadPromise?: Promise<ort.InferenceSession>;
  private readonly modelUrl: string;
  private readonly eps: ('webgpu' | 'wasm' | 'cpu')[];
  private readonly wasmPaths?: string;
  private readonly inputName: string;
  private readonly outputName: string;

  constructor(opts: OnnxClassifierOptions = {}) {
    this.session = opts.session;
    this.modelUrl = opts.modelUrl ?? 'doc-cnn.onnx';
    this.eps = opts.executionProviders ?? ['wasm'];
    this.wasmPaths = opts.wasmPaths;
    this.inputName = opts.inputName ?? 'input';
    this.outputName = opts.outputName ?? 'logits';
  }

  private load(): Promise<ort.InferenceSession> {
    if (this.session) return Promise.resolve(this.session);
    if (this.wasmPaths) {
      ort.env.wasm.wasmPaths = this.wasmPaths;
      ort.env.wasm.numThreads = 1; // avoid the cross-origin-isolation requirement
    }
    this.loadPromise ??= ort.InferenceSession.create(this.modelUrl, {
      executionProviders: this.eps,
    }).then((s) => (this.session = s));
    return this.loadPromise;
  }

  async run(chw: Float32Array, size: number): Promise<number[]> {
    const session = await this.load();
    const dims = [1, 3, size, size];
    const out = await session.run({ [this.inputName]: new ort.Tensor('float32', chw, dims) });
    const tensor = out[this.outputName] ?? out[session.outputNames[0]];
    return Array.from(tensor.data as Float32Array);
  }
}
